<template>
  <div
    class="download"
    :class="{ onlyitem: onlyItem, generating: isGenerating }"
  >
    <a
      class="download-link more-black"
      @click="onDownload"
      :data-payload="file.payload || file.url"
      :data-protected="file.isProtected || 'false'"
      :data-targetid="downloadFormId"
      >{{ fileName }}</a
    >
    <div class="generating-message">{{ labels.generating }}</div>
  </div>
</template>

<script>
import Config from "../services/config.js";
import Utils from "../services/utils.js";
import DataLayer from "../services/datalayer.js";
import Labels from "../services/labels.js";
import emitter from "tiny-emitter/instance";

export default {
  name: "DownloadFile",
  props: ["file", "only-item", "parent-number", "parent-name"],
  data() {
    return { isGenerating: false, canDownload: true, labels: Labels, downloadFormId: "" };
  },
  computed: {
    fileName() {
      const file = this.$props.file;
      if (!file) return "";
      if (file.regionName) {
        let name = Config.sdsFileTitleFormat;
        name = name.replace("{country}", file.regionName);
        name = name.replace("{version}", file.versionText);
        name = name.replace("{language}", file.languageName);
        return name;
      } else {
        return `${file.title} ${file.size}`;
      }
    },
  },
  mounted() {
    //we just take the first download form on the page, if one exists
    let el = document.querySelector(".download-form-popup.overlay");
    if (el) {
      this.downloadFormId = el.id;
    }
    emitter.on("download-started", (e) => {
      this.canDownload = false;
    });
    emitter.on("download-complete", (e) => {
      this.canDownload = true;
    });
  },
  destroyed() {
    emitter.off("download-started");
    emitter.off("download-complete");
  },
  methods: {
    updateMaterialNumber(file, parent) {
      if (!file || !file.url) return "";
      if (!parent) return file.url;
      //if this is the main product, we don't do anything
      if (parent.length === 6) return file.url;
      let paddedParent = parent.padStart(18, "0");
      return file.url.replace(file.materialNumber, paddedParent);
    },
    updateFileName(original, parent) {
      if (!original) return "";
      if (!parent) return "";
      var safeName = Utils.makeSafe(parent);
      return original.replace(this.$props.file.materialName, safeName);
    },
    onDownload() {
      const file = this.$props.file;
      if (!file) return;
      if (file.regionName) {
        //this is an SDS...we trigger the generation and download it in memory, then we save it
        let self = this;
        if (self.isGenerating) {
          //we avoid accidental double-clicks
          return;
        }

        if (!this.canDownload) {
          Utils.showStatusMessage(Labels.pleaseWait, "yellow");
          return;
        }

        self.isGenerating = true;
        emitter.emit("download-started");
        if (file.isProtected && file.payload && this.downloadFormId) {
          console.log(
            "This seems to be an EH&S file. Falling back to the download box behavior."
          );
          //this should be caught by the download form
          //so we just spin the loader for 5 seconds
          let actualFormId =
            this.downloadFormId.indexOf("download_form_") === 0
              ? this.downloadFormId.substring(14)
              : this.downloadFormId;
          window.cla &&
            window.cla.component &&
            window.cla.component.portlet &&
            window.cla.component.portlet.downloadform &&
            window.cla.component.portlet.downloadform.downloadPayload(
              file.payload,
              file.isProtected,
              actualFormId
            );
          setTimeout(() => {
            self.isGenerating = false;
          }, 15000);
        } else {
          let modifiedUrl = self.updateMaterialNumber(
            file,
            self.$props.parentNumber
          );
          DataLayer.downloadFile(modifiedUrl)
            .then((res) => {
              self.isGenerating = false;
              self.$root.$emit("download-complete");
              if (res.data) {
                var blob = new Blob([res.data], {type: "application/pdf"});
                var link = document.createElement("a");
                var fileName = self.updateFileName(
                  file.fileName,
                  self.$props.parentName
                );
                if (window.navigator.msSaveOrOpenBlob) {
                  link.onclick = function (e) {
                    window.navigator.msSaveOrOpenBlob(blob, fileName);
                  };
                } else {
                  link.href = window.URL.createObjectURL(blob);
                  link.download = fileName;
                }

                document.body.appendChild(link);

                link.click();

                document.body.removeChild(link);
                Utils.reportToAnalytics("DocFinder SDS Download", fileName);
              }
            })
            .catch((err) => {
              self.isGenerating = false;
              self.$root.$emit("download-complete");
              Utils.showStatusMessage(`Unable to generate file: ${err}`, "red");
            });
        }
      } else {
        //this is a product file...we jump through the decoding hoops
        var decoded = Base64.decode(file.url);
        var tmp = decoded.split("|");
        if (tmp.length != 2) {
          console.error("Malformed file URL");
          return;
        }
        var correctedUrl = tmp[1].replace("/sitecore/shell", "");
        /*if (correctedUrl.indexOf("/-/media") === 0) {
          //we add the host, for testing
          correctedUrl = "http://clariant.dev.local" + correctedUrl;
        }*/
        this.$root.$children[0].$refs.downloadForm.recordDownload(
          tmp[0],
          correctedUrl,
          function () {
            window.open(correctedUrl, "_self");
            Utils.reportToAnalytics(
              "DocFinder File Download",
              `${file.title} ${file.size}`
            );
          },
          function (err) {
            Utils.showStatusMessage(
              "Error downloading file: " + err.message,
              "red"
            );
          }
        );
      }
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.download {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8'%3E%3Cg fill='%236A6A6A' fill-rule='nonzero'%3E%3Cpath d='M0 8h10V7H0zM9 .003L5 5 1 0z'/%3E%3C/g%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 98% center;
  background-size: 10px 8px;
  border-bottom: 1px solid #c9c9c9;
  height: 45px;
  display: flex;
  align-items: center;
  font-family: "Gotham SSm A", "Gotham SSm B", sans-serif;
  font-size: 12px;
  line-height: 19px;
  font-weight: 700;
  text-transform: uppercase;
}

.download > a {
  color: #000;
}

.download.generating {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg class='lds-spinner' width='64px' height='64px' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid' style='background: none;'%3E%3Cg transform='rotate(0 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.9166666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(30 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.8333333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(60 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.75s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(90 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.6666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(120 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5833333333333334s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(150 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.5s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(180 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.4166666666666667s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(210 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.3333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(240 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.25s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(270 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.16666666666666666s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(300 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='-0.08333333333333333s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3Cg transform='rotate(330 50 50)'%3E%3Crect x='47' y='24' rx='9.4' ry='4.8' width='6' height='12' fill='%233498DB'%3E%3Canimate attributeName='opacity' values='1;0' keyTimes='0;1' dur='1s' begin='0s' repeatCount='indefinite'%3E%3C/animate%3E%3C/rect%3E%3C/g%3E%3C/svg%3E");
  background-size: 32px;
  background-position: 99% center;
}

.download:first-of-type,
.onlyitem.download {
  border-top: 1px solid #c9c9c9;
}

.download-link {
  padding-right: 45px;
  padding-left: 0;
  background-image: none;
  font-weight: 400;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  flex-grow: 1;
}

.download-link > div {
  display: flex;
  justify-content: space-between;
}

.generating-message {
  padding-right: 35px;
  font-family: "Gotham SSm A", "Gotham SSm B", sans-serif;
  font-size: 13px;
  line-height: 19px;
  display: none;
}

.download.generating .generating-message {
  display: block;
}

@media all and (max-width: 780px) {
  .download.generating > a {
    display: none;
  }
}
</style>